import React, {
	useMemo,
	useState,
	useEffect,
	useContext,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import type { MediaItem } from '../../stores/types/media';
import { ConnectedUser } from '../../stores/types/connection';
import {
	contrastingColor, getNameOrInitials, toMap,
} from '../../utils/utils';
import { VideoStream } from '../types/videos';
import { GreenroomState as GRS } from '../../stores/types/types';
import ThumbnailBadge from './thumbnail-badge';
import useShots from '../../contexts/shots/provider';
import useUserActions from '../../hooks/use-user-actions';
import { RESTRICTED_USER_STATES } from '../../stores/reducers/session';
import * as MediaActions from '../../stores/actions/media';
import { setConnectedPresenterVideos } from '../../stores/actions/video';
import { getCurrentPageThumb, pageExists } from '../../utils/media';
import { cleanupVideoStreamElement } from '../../utils/video/video-streams';
import useDragItem from '../../hooks/use-dnd/use-drag-item';
import type { DragItem } from '../../stores/types/user-interface';
import { DRAG_TYPE } from '../../stores/reducers/user-interface';
import Thumbnail from '../chat/chat-document-thumbnail';
import VideoFeedContextMenu from './video-feed-context-menu';
import { setOpenContextMenuId } from '../../stores/actions/user-interface';
import { AVContext } from '../../contexts/audio-video';
import { getUserWebRTCIdMap } from '../../utils/user';
import { ROOM_STATES } from '../../stores/reducers/settings';
import FeedDebugOverlay from './video-debug-ui';
import RestrictedUserTooltip from './restricted-user-tooltip';
import OpenTok from '../../utils/video/opentok-connection';
import { Connection } from '@opentok/client';
import { showAlert } from '../general-components/alert-service';
import useProfile from '../../hooks/use-profile';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import VideoFeedTelemetry from './video-feed-telemetry';
import colors from '../../utils/colors';
import FeedPhoneDialer from './feed-phone-dialer';
import Iconv2 from '../general-components/icon-v2/icon';
import { HangupPhone } from '../../connection/phone';

interface Props {
	video: VideoStream;
	socket: any;
	opentok?: OpenTok | null;
	feedsRef?: React.MutableRefObject<HTMLDivElement | null>;
	dropCallback?: (item: DragItem, dropData: any) => void;
}

function nullClick(e: any) {
	e.stopPropagation();
	e.currentTarget.blur();
}

const TELEMETRY_THRESHOLD = 0.05;

export default function VideoFeedThumbnail(props: Props) {
	const dispatch = useDispatch();
	const {
		video,
		socket,
		opentok,
		dropCallback = (item: DragItem, dropData: any) => {
			console.log('No drop callback provided', item, dropData);
		},
	} = props;

	const host = useSelector((s: GRS) => s.SessionReducer.host);
	const webrtcID = useSelector((s: GRS) => s.SessionReducer.webrtcID);
	const connectedUsers = useSelector((s: GRS) => s.SessionReducer.connectedUsers);
	const embed = useSelector((s: GRS) => s.SessionReducer.embed);
	const meeting = useSelector((s: GRS) => s.SessionReducer.meeting);
	const user = useSelector((s: GRS) => s.SessionReducer.user);
	const restrictedUserState = useSelector((s: GRS) => s.SessionReducer.restrictedUserState);
	const connectedPresenterVideos = useSelector((s: GRS) => s.VideoReducer.connectedPresenterVideos);
	const mainVideoStream = useSelector((s: GRS) => s.VideoReducer.mainVideoStream);
	const media = useSelector((s: GRS) => s.MediaReducer.media);
	const presentedMedia = useSelector((s: GRS) => s.MediaReducer.presentedMedia);
	const backstageCommsActive = useSelector((s: GRS) => s.SettingsReducer.backstageCommsActive);
	const shotStartTimestampEntity = useSelector(
		(s: GRS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const roomState = useSelector((s: GRS) => s.MeetingReducer.roomState);
	const backstageCommsTargetWebRtcId = useSelector(
		(s: GRS) => s.SessionReducer.backstageCommsTargetWebRtcId,
	);
	const useMasks = useSelector((s: GRS) => s.SettingsReducer.featureFlags.use_profile_masks);
	const usePstn = useSelector((s: GRS) => s.SettingsReducer.featureFlags.use_pstn);

	const connectionId = opentok?.streams.get(video.id)?.connection.connectionId;
	const [videoMap, setVideoMap] = useState<Map<string, ConnectedUser>>(toMap('webrtcID', connectedUsers));
	const [rightClickCoords, setRightClickCoords] = useState<[number, number]>();
	const [audioLevel, setAudioLevel] = useState(0);
	const [dialerOpen, setDialerOpen] = useState(false);
	// Tracks a pending call before the SIP stream arrives from OpenTok.
	const [callPending, setCallPending] = useState(false);

	const token = useSelector((state: GRS) => state.SessionReducer.token);
	const meetingId = useSelector((state: GRS) => state.SessionReducer.meeting?.meeting_id);

	const hasActivePhoneCall = useMemo(
		() => video.profile
			? connectedPresenterVideos.some(
				(v: VideoStream) => v.isPhoneCall && v.profile === video.profile,
			)
			: false,
		[connectedPresenterVideos, video.profile],
	);

	// Clear callPending once the real SIP stream has arrived or when no longer active.
	useEffect(() => {
		if (hasActivePhoneCall) setCallPending(false);
	}, [hasActivePhoneCall]);

	const callIsActive = hasActivePhoneCall || callPending;

	const handleHangup = async () => {
		if (!token || !meetingId) return;
		setCallPending(false);
		await HangupPhone(token, meetingId);
	};

	const wrapperRef = useRef<HTMLDivElement | null>(null);

	const { getAV } = useContext(AVContext);
	const { video: hasVideo, audio: hasAudio } = getAV(video?.id);

	const {
		idsOfVideosInCanvas,
		isOnCanvas,
	} = useShots();

	const {
		backstageCommsToggle,
		toggleAudio,
		toggleVideo,
		getFeedName,
	} = useUserActions();

	const suggestedProfile = video.id === 'local' ? user?.gr_user : video.profile;

	const {watchedProfile} = useProfile({suggestedProfile});
	const flaggedProfile = useMasks ? watchedProfile : videoMap.get(video.id);

	// The right click context menu requires a unique string id
	const globalMappedUser = useMemo(() => (video.id !== 'local' ? videoMap.get(video.id) : user), [video, videoMap, user]);
	// If the global mapped user is not available, this means it's a sip feed and we use the id as the context menu id
	const contextMenuId = useMemo(() => `video-feed-${globalMappedUser?.gr_user ?? (video.isSipCall && video.id)}`, [globalMappedUser]);

	const [hasCam, hasMic] = useMemo(() => [
		!!video.srcObject?.getVideoTracks().length,
		!!video.srcObject?.getAudioTracks().length,
	], [video.srcObject]);

	// A permission revoke suspends (not destroys) the publisher, so hasCam/hasMic stay true —
	// gate on the restricted state instead: local Redux for self, socket-broadcast state for remotes.
	const isSelfFeed = video.id === webrtcID;
	const feedRestrictedState = isSelfFeed
		? restrictedUserState
		: (videoMap.get(video.id)?.restrictedUserState ?? RESTRICTED_USER_STATES.NONE);
	const camRestricted = [
		RESTRICTED_USER_STATES.CAMERA,
		RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE,
	].includes(feedRestrictedState);
	const micRestricted = [
		RESTRICTED_USER_STATES.MICROPHONE,
		RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE,
	].includes(feedRestrictedState);

	// Added unused dependency to test fix for broken presenter feeds.
	// green-4902
	useEffect(() => {
		if (
			mainVideoStream
			&& (!!video.self || !!video.local)
			&& !video.isMobile
			&& (video.srcObject !== mainVideoStream)
		) {
			const updatedPresenterVideos = connectedPresenterVideos.map((v: VideoStream) => {
				const newV = v;
				if (!!newV.self || !!newV.local) {
					newV.srcObject = mainVideoStream;
				}
				return newV;
			});
			dispatch(setConnectedPresenterVideos(updatedPresenterVideos));
		}
	}, [video, mainVideoStream, connectedPresenterVideos]);

	const hasMultipleProducers = useMemo(() => connectedUsers
		.filter((u: ConnectedUser) => u.isProducer).length ?? 0 > 1, [connectedUsers]);

	const handlePlaying = (e: any) => {
		e.target.volume = 0;
	};

	const canEditMedia = useMemo(() => {
		if (host) return true;
		if (!user) return false;

		const mediaItem = media?.find((med: MediaItem) => med.id === video?.mediaId);

		// if it has been shared with you
		if (mediaItem?.shared_with?.includes(user.gr_user.toString())) { return true; }

		// if it's your media
		return mediaItem?.created_by === user?.gr_user;
	}, [host, presentedMedia, user, video, media]);

	const presentedMediaItem = useMemo(() => presentedMedia
		.find((item) => item.mediaId === video.mediaId), [video, presentedMedia]);
	const mediaItem = useMemo(() => media
		.find((item) => item.id === video.mediaId), [video, media]);

	/* this is a duplicate fn in feed-media.tsx, media-slides-view.tsx,
	and video-feed-thumbnail.tsx */
	const changePage = (newPage: number) => {
		console.log('video-feed-thumbnail.tsx changePage', { presentedMediaItem, newPage });
		if (
			!mediaItem
			|| !presentedMediaItem
			|| !socket
			|| !pageExists(newPage, mediaItem)
		) return;

		const newVideoState = { paused: !shotStartTimestampEntity, timeElapsed: 0, volume: 1 };

		dispatch(MediaActions.setPresentedMediaPage(
			presentedMediaItem.mediaId,
			newPage,
			newVideoState,
		));
	};

	const getPosterImage = (v: VideoStream): string => {
		const mappedUser = videoMap.get(v.id);
		if (mappedUser && (mappedUser.poster || mappedUser.avatar)) {
			return mappedUser.poster || mappedUser.avatar;
		} if (user && v.id === 'local') {
			return user.bg_image ?? user.avatar ?? '';
		} if (v.poster) {
			return v.poster;
		}
		return '';
	};

	const thumbnailUrl = useMemo(
		() => getCurrentPageThumb(mediaItem?.media_data, presentedMediaItem?.currentPage),
		[presentedMediaItem?.currentPage, mediaItem?.media_data],
	);

	useEffect(() => {
		setVideoMap(getUserWebRTCIdMap(connectedUsers));
	}, [connectedUsers]);

	const dndProps = useMemo<DragItem>(() => {
		if (video.isMedia && video.mediaId) {
			return { id: video.mediaId, type: DRAG_TYPE.FEED_MEDIA };
		} if (video.isScreenShare) {
			const screenShareUser = connectedUsers.find(
				(connectedUser: ConnectedUser) => connectedUser.screenShareWebrtcId === video.id,
			);
			if (screenShareUser) {
				return { id: screenShareUser.email, type: DRAG_TYPE.FEED_SCREENSHARE };
			}
		} else if (video.isSipCall) {
			return { id: video.id, type: DRAG_TYPE.FEED_SIP_CALL };
		} else if (video.profile) {
			return { id: video.profile, type: DRAG_TYPE.FEED_PRESENTER };
		}

		return { id: 0, type: DRAG_TYPE.FEED_EMPTY };
	}, [video, connectedUsers]);

	const { dragRef, isDragging } = useDragItem(dndProps, host);

	const kickUser = () => {
		opentok?.session.forceDisconnect(connectionId as unknown as Connection, (error) => {
			if (error) {
				showAlert({
					message: 'Error',
					description: 'Failed to kick user',
					duration: 2000,
					});
			} else {
				console.log('User kicked');
			}
		});
	};

	useEffect(() => {
		// Find the corresponding subscriber for the video
		const subscriber = opentok?.subscribers?.[video.id];
		const publisher = opentok?.publisher;

		const handleAudioLevel = (event: { audioLevel: number }) => {
			const { audioLevel: raw } = event;
			// Ignore anything below the threshold
			if (raw < TELEMETRY_THRESHOLD) {
				setAudioLevel(0);
			} else {
				// Normalize to 0–100 assuming 0.2 is the loudest
				const level = Math.min(100, Math.floor((raw / 0.2) * 100));
				setAudioLevel(level);
			}
		};

		// Subscriber
		if (subscriber) {
			subscriber.on('audioLevelUpdated', handleAudioLevel);
		}

		// Skip the audio-level meter on an audio-less (camera-only) publisher — Vonage's
		// meter reads a non-existent analyser and crashes in its RAF loop.
		if (publisher && video.self && publisher.getAudioSource?.()) {
			publisher.on('audioLevelUpdated', handleAudioLevel);
		}

		// eslint-disable-next-line consistent-return
		return () => {
			if (subscriber) {
				subscriber.off('audioLevelUpdated', handleAudioLevel);
			}
			if (publisher && video.self) {
				publisher.off('audioLevelUpdated', handleAudioLevel);
			}
		};
	}, [opentok?.subscribers, video.id]);

	const { dropRef } = useDropArea({
		accepts: DRAG_TYPE.PROFILE_MASK,
		dropCallback: dropCallback,
		dropAreaData: {
			id: watchedProfile?.email ?? 'none',
		},
	});

	return (
		<>
			{isDragging && (<div className="video-thumb-drag-placeholder" />)}
			{(!!globalMappedUser?.gr_user || (video.isSipCall && !!video.id )) && !video.isScreenShare && (
				<VideoFeedContextMenu
					clickCoordinates={rightClickCoords}
					contextMenuId={contextMenuId}
					socket={null}
					userId={globalMappedUser?.gr_user || video.id as unknown as number}
					webRTCId={video.id}
					kickUser={video.isSipCall ? kickUser : undefined}
				/>
			)}
			<div
				data-testid="video-feed-thumbnail"
				ref={dragRef}
				onContextMenu={(e) => {
					if (video.isScreenShare) {
						e.preventDefault();
						return;
					}
					if (host) {
						e.preventDefault();
						setRightClickCoords([e.pageX, e.pageY]);
						dispatch(setOpenContextMenuId(contextMenuId));
					}
				}}
				onFocus={(e) => e.currentTarget.blur()}
				id={String(video.id)}
				className={classNames('video-thumb', {
					'producer-thumbnail-large': !host && videoMap.get(video.id)?.isProducer && !hasMultipleProducers,
					'is-dragging': isDragging,
					'cursor-grab': host,
				})}
			>
				{hasAudio && <VideoFeedTelemetry level={audioLevel}/>}
				<div className="video-wrapper" ref={wrapperRef}>
					{
						video.isMedia ? (
							<div
								className={classNames('media-thumbnail-img-container', mediaItem?.media_data.fill && 'fill')}
								style={{ background: mediaItem?.media_data?.background_color }}
							>
								<Thumbnail thumbnail={thumbnailUrl} />
							</div>
						) : (
							<video
								autoPlay
								muted
								playsInline
								onPlaying={handlePlaying}
								ref={(ref: any) => {
									// Attaches connectedPresenterVideo.srcObject
									// to the presenter video feed DOM element.
									// Could cause issues like a presenter video
									// appearing as a black feed.
									// green-4902
									if (ref && ref.srcObject !== video.srcObject) {
										const refRef = ref;
										refRef.srcObject = video.srcObject as MediaStream;
										cleanupVideoStreamElement(ref);
									}
								}}
								style={{ pointerEvents: 'none', transform: video.self ? 'scaleX(-1)' : 'none' }}
							/>
						)
					}

					{!embed && (
						<>
							<div className={classNames('video-overlay')} ref={dropRef}>
								{!hasVideo && (
									<div className={classNames('poster')}>
										{((watchedProfile?.bg_image && useMasks) || getPosterImage(video)) ? (
											<img className="poster-image" src={(useMasks && watchedProfile?.bg_image) 
												? watchedProfile.bg_image
												: getPosterImage(video)} alt={''} />
										) : (
											<div className="no-image-placeholder">
												<span style={{
													backgroundColor: meeting?.primary_color ? `#${meeting.primary_color}` : '#333',
													color: contrastingColor(meeting?.primary_color ?? colors.gray33),
												}}>
													{getNameOrInitials(flaggedProfile, false)}
												</span>
											</div>
										)}
									</div>
								)}

								<RestrictedUserTooltip
									restrictedUserState={videoMap.get(video.id)?.restrictedUserState}
								/>

								<ThumbnailBadge
									isScreenShare={!!video.isScreenShare}
									videoIsOnCanvas={idsOfVideosInCanvas.includes(video.id)}
									userObjForFeed={videoMap.get(video.id)}
									videoWebRtcId={video.id}
								/>

								<FeedDebugOverlay feedWebRtcId={video.id} />

								<div className="video-overlay-bottom">
									<label className={classNames('font-size-14', 'font-weight-heavy', !host && videoMap.get(video.id)?.isProducer && !hasMultipleProducers ? 'is-producer' : '')}>
										{getFeedName(video)}
									</label>
									{host && usePstn && !video.isSipCall && !video.isMedia && !video.isScreenShare && (
										<button
											className={classNames('feed-phone-btn', { active: callIsActive })}
											onClick={(e) => {
												e.stopPropagation();
												if (callIsActive) { handleHangup(); } else { setDialerOpen((o) => !o); }
											}}
											title={callIsActive ? 'Hang up' : 'Dial phone'}
										>
											<Iconv2 name={ICONS.PHONE} size={12} color={callIsActive ? COLORS.GREEN : COLORS.WHITE} />
										</button>
									)}
								</div>
								{dialerOpen && !callIsActive && usePstn && !video.isSipCall && !video.isMedia && !video.isScreenShare && (() => {
									const mapped = globalMappedUser ?? videoMap.get(video.id);
									return (
										<FeedPhoneDialer
											identity={{
												gr_user: mapped?.gr_user ?? video.profile,
												email: mapped?.email,
												first_name: watchedProfile?.first_name ?? mapped?.first_name,
												last_name: watchedProfile?.last_name ?? mapped?.last_name,
												poster: (mapped as ConnectedUser)?.poster ?? mapped?.avatar,
												phone: watchedProfile?.phone ?? mapped?.phone ?? (video.id === 'local' ? user?.phone : undefined),
											}}
											onClose={() => setDialerOpen(false)}
											onCallStarted={() => { setCallPending(true); setDialerOpen(false); }}
										/>
									);
								})()}

								{(host || (video.id === webrtcID))
									&& !video.isMedia
									&& videoMap.get(video.id)
									&& videoMap.get(video.id)?.socketId
									&& (
									<div className="video-options">
										{hasMic && !micRestricted && (
											<button
												className={hasAudio ? '' : 'muted'}
												onClick={(e) => {
													nullClick(e);
													toggleAudio(video.id);
												}}>
												<Icon
													name={hasAudio ? ICONS.MICROPHONE_ON : ICONS.MICROPHONE_OFF}
													color={COLORS.WHITE}
													size={16}/>
											</button>
										)}

										{hasCam && !video.isScreenShare && !camRestricted && (
											<button className={hasVideo ? '' : 'muted'}
												onClick={(e) => {
													nullClick(e);
													toggleVideo(video.id);
												}}>
												<Icon
													name={hasVideo ? ICONS.CAMERA_ON : ICONS.CAMERA_OFF}
													color={COLORS.WHITE}
													size={16}/>
											</button>
										)}

										{
											host
											&& !video.isScreenShare
											&& video.id !== webrtcID
											&& idsOfVideosInCanvas.includes(video.id)
											&& backstageCommsActive
											&& !isOnCanvas
											&& roomState === ROOM_STATES.SHOWTIME
											&& !videoMap.get(video.id)?.onsiteLocationSession?.id
											&& <button
												className={classNames({
													muted: video.id === backstageCommsTargetWebRtcId,
													disabled: restrictedUserState !== RESTRICTED_USER_STATES.NONE,
												})}
												onClick={nullClick}
												onMouseDown={(e) => {
													nullClick(e);
													backstageCommsToggle(false, video.id);
												}}
												onMouseUp={(e) => {
													nullClick(e);
													backstageCommsToggle(true, video.id);
												}}
												onMouseLeave={(e) => {
													nullClick(e);
													backstageCommsToggle(true, video.id);
												}}
											>
												<Icon name={ICONS.HEADPHONES} color={COLORS.WHITE} size={16} />
											</button>
										}

									</div>
									)}

								{
									canEditMedia
									&& video.isMedia
									&& presentedMediaItem
									&& mediaItem
									&& !video.isScreenShare
									&& (
										!idsOfVideosInCanvas.includes(video.id)
										|| mediaItem.num_pages > 1
									)
									&& (
										<div className="video-options media">
											{mediaItem.num_pages > 1
												? <>
												<button className={classNames({ muted: video.muted })} onClick={(e) => {
													e.stopPropagation();
													changePage((presentedMediaItem?.currentPage ?? 0) + 1);
													e.currentTarget.blur();
												}}>
													<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} color={COLORS.WHITE} size={16} />
												</button>
												<button className={classNames({ muted: video.muted })} onClick={(e) => {
													e.stopPropagation();
													changePage((presentedMediaItem?.currentPage ?? 2) - 1);
													e.currentTarget.blur();
												}}>
													<Icon name={ICONS.KEYBOARD_ARROW_LEFT} color={COLORS.WHITE} size={16} />
												</button>
												</>
												: <></>
										}

									</div>
									)}
							</div>
							<div className={classNames('selection-border', idsOfVideosInCanvas.includes(video.id) && 'in-shot')} />
						</>
					)}
				</div>
			</div>
		</>
	);
}
